import React, {useMemo, useState} from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import {useTheme} from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import {BasicModalContent} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {Box} from "@mui/system";
import {InstalledBaseNode} from "../../codegen/graphql";
import NodeModalTitle from "../InstalledBaseTreeColumn/NodeModalTitle";
import InstalledBaseNodePathBreadcrumb from "../InstalledBaseNodePathBreadcrumb";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import {useKeycloak} from "@react-keycloak/web";
import {UpdateInstalledBaseNode} from "../../graphql/mutations/updateInstalledBaseNode";
import {toast} from "react-toastify";
import _ from "lodash";
import {InstalledBaseNodeTdas} from "../../graphql/queries/installedBaseNodeTdas";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import {withNodeImageInputType} from "../NodeImageInput";
import {separateInstalledBaseNodeAttributes} from "../../utils/separateInstalledBaseNodeAttributes";
import {applyInstalledBaseNodeValueRules} from "../../utils/installedBaseNodeFormRules";
import {
    getNodeImageChange,
    NODE_IMAGE_FIELD,
    NODE_IMAGE_REFETCH_QUERIES,
    prepareNodeImageFormValues,
} from "../../utils/nodeImage";
import {uploadSingleFile} from "../../utils/uploadSingleFile";

interface Props {
    node: InstalledBaseNode;
    refetchFunction?: () => void;
    open: boolean,
    onClose?: () => void,
}

const EditNodeModal = ({node, refetchFunction, onClose, open}: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {t} = useTranslation();

    const [formikHelpers, setFormikHelpers] = useState<{
        setFieldValue: (field: string, value: any) => void;
        setValues: (values: { [key: string]: any }) => void;
        resetForm: () => void;
    } | null>(null);

    const [updateNode] = useMutation(UpdateInstalledBaseNode);
    const {keycloak} = useKeycloak();

    const {data} = useQuery(InstalledBaseNodeTdas, {
        variables: {
            id: node?.id,
        },
        fetchPolicy: "network-only",
    })

    const handleSubmit = async (formValues: any) => {
        // A newly picked node image is uploaded first and its file-service UUID
        // becomes input.imageId; an untouched image is omitted (absent key = keep).
        const initialImageId = initialFormValues[NODE_IMAGE_FIELD];
        const imageChanged = getNodeImageChange(formValues, initialImageId).kind !== 'unchanged';
        let preparedValues: Record<string, any>;
        try {
            preparedValues = await prepareNodeImageFormValues(
                formValues,
                initialImageId,
                (file) => uploadSingleFile(file, keycloak.token)
            );
        } catch (err) {
            toast.error(t('Image upload failed.'));
            return;
        }

        const { input, dynamicLists } = separateInstalledBaseNodeAttributes(
            preparedValues,
            rawAttributes
        );

        return updateNode({
                variables: {
                    id: node?.id,
                    input,
                    ...Object.fromEntries(
                        Object.entries(dynamicLists).map(([key, list]) => [key, list.length > 0 ? list : undefined])
                    )
                },
                // Every nameplate / tile / file list showing the image re-reads it.
                refetchQueries: imageChanged ? NODE_IMAGE_REFETCH_QUERIES : undefined,
            }
        ).then((res) => {
            toast.success(t('Node has been updated successfully.'));
            if (refetchFunction) {
                refetchFunction();
            }
            if (onClose) {
                onClose();
            }
        }).catch((err) => {
            console.log(err, 'ERROR')
            toast.error(resolveErrorMessage(err, t))
        })
    }

    const handleValuesChange = (values: { [key: string]: any }) => {
        applyInstalledBaseNodeValueRules(values, formikHelpers);
    };

    const rawAttributes = _.get(data, 'installedBaseNode.typeDefinitionAttributeList.typeDefinitionAttributes', [])
    // Thumbnail of the current node image for the image input's preview.
    const imagePreviewUrl = _.get(data, 'installedBaseNode.image.file.thumbnailLink') as string | undefined;
    const formAttributes = useMemo(
        () => withNodeImageInputType(rawAttributes, imagePreviewUrl),
        [rawAttributes, imagePreviewUrl]
    );

    // Create initial form values from attribute values
    const initialFormValues = useMemo<Record<string, any>>(() => {
        if (!rawAttributes.length) return {};
        return rawAttributes.reduce((acc: { [key: string]: any }, attr: any) => {
            acc[attr.index] = attr.value || '';
            return acc;
        }, {});
    }, [rawAttributes]);

    return <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
        <BasicModalContent
            title={<NodeModalTitle prefix={t('Edit')} node={node} labelType={'title'}/>}
            onClose={onClose}
        >
            <Box sx={{mt: 0.5, mb: 1}}>
                <InstalledBaseNodePathBreadcrumb nodeId={node?.id} excludeSelf/>
            </Box>
            <InstalledBaseTDAForm
                formStyle={{paddingTop: '0px'}}
                onSubmit={handleSubmit}
                onCancel={onClose}
                submitButtonText={t('Save')}
                attributes={formAttributes}
                initialFormValues={initialFormValues}
                enableReinitialize={true}
                onValuesChange={handleValuesChange}
                setFormikHelpersRef={setFormikHelpers}
            />
        </BasicModalContent>
    </Dialog>
};

export default EditNodeModal;
